"use client";

import { IconCaretDownFilled } from "@tabler/icons-react";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { DropdownMenu } from "radix-ui";
import { Button } from "@/components/ui/Button";
import { Link, usePathname } from "@/i18n/navigation";
import type { AppLocale } from "@/i18n/routing";
import { cn } from "@/lib/cn";

/**
 * Switches language while staying on the current page.
 */
export function LocaleSwitcher({ locales }: { locales: AppLocale[] }) {
  const t = useTranslations("localeSwitcher");
  const active = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (locales.length < 2) return null;

  const query = searchParams.toString();
  const href = query ? `${pathname}?${query}` : pathname;

  return (
    <div className="sort grid auto-cols-max grid-flow-col items-center justify-self-end text-right text-xs">
      <div className="relative inline-block">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              variant="ghost"
              size="xs"
              aria-label={t("label")}
              className="group"
            >
              <span className="text-sm font-normal">
                {active.toUpperCase()}
              </span>
              <IconCaretDownFilled
                size={16}
                aria-hidden
                className="group-data-[state=open]:rotate-180"
              />
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Content
            aria-label={t("label")}
            sideOffset={14}
            className={cn(
              "rounded-b-xl flex flex-col",
              "border border-t-0 border-gray-300 bg-white dark:bg-dark-color"
            )}
          >
            {locales.map((locale) => (
              <DropdownMenu.Item key={locale} asChild>
                <Link
                  locale={locale}
                  href={href}
                  role="menuitemradio"
                  aria-checked={locale === active}
                  className={cn(
                    "flex select-none px-4 py-2 text-left text-sm",
                    "hover:bg-primary hover:dynamic-text dark:hover:bg-primary",
                    locale === active
                      ? "bg-primary dynamic-text"
                      : "bg-white dark:bg-black",
                    "last:rounded-b-lg"
                  )}
                >
                  {locale.toUpperCase()}
                </Link>
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}
